import React, { useEffect, useState } from 'react';
import useInView from '../hooks/useInView';
import useMotionPrefs from '../hooks/useMotionPrefs';

/* ==================================================================
   AnimatedCounter
   ------------------------------------------------------------------
   Counts up once the number scrolls into view, and re-runs if the
   value itself changes (live stats can land after first paint).
   Under reduced motion it simply prints the final figure.
   ================================================================== */

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

export default function AnimatedCounter({
  value = 0,
  suffix = '',
  duration = 1600,
  className = '',
}) {
  const { reduced } = useMotionPrefs();
  const [ref, inView] = useInView({ threshold: 0.35, skip: reduced });
  const [display, setDisplay] = useState(reduced ? value : 0);

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return undefined;
    }
    if (!inView) return undefined;

    let frame = 0;
    let start = 0;

    const tick = (now) => {
      if (!start) start = now;
      const t = Math.min((now - start) / duration, 1);
      setDisplay(Math.round(value * easeOutExpo(t)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, value, duration, reduced]);

  return (
    <span ref={ref} className={className}>
      {display.toLocaleString('en-IN')}
      {suffix}
    </span>
  );
}
